import { Formik } from 'formik';
import pluralize from 'pluralize';
import { rgba } from 'polished';
import React, { useRef } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { BillableIcon, Field, Form, FormMessage, Tooltip } from '~/components';
import FixedDropdown from '~/components/FixedDropdown';
import { useApi, useWorkspace } from '~/contexts';
import { colors } from '~/styles';
import TimeEntryDetails from './TimeEntryDetails';
import { BulkActionModal } from '~/components/bulk-action';

const BillingNote = styled(FormMessage)`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: ${colors.black};
  background-color: ${rgba(colors.warning, 0.18)};
`;

// The width of the role input inside the 42rem bulk action modal.
const RoleMenu = styled(FixedDropdown.Menu)`
  max-width: 38.75rem;
`;

function RoleDropdown(props) {
  return <FixedDropdown {...props} />;
}

RoleDropdown.Menu = RoleMenu;

const RoleOption = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;

  > div {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

export default function BulkSetRole({ ids, project, onClose, onComplete }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const formRef = useRef();

  return (
    <BulkActionModal
      title="Bulk Set Role"
      onClose={onClose}
      onComplete={onComplete}
      onConfirm={() => formRef.current?.submitForm()}
      renderRow={TimeEntryDetails}>
      <Formik
        innerRef={formRef}
        initialValues={{ projectRole: null }}
        onSubmit={async (values) => {
          return api.www
            .workspaces(workspace.id)
            .timeAdmin()
            .bulkSetRole(ids, { projectRoleId: values.projectRole.id })
            .then((r) => r.data);
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          projectRole: Yup.mixed().label('Role').required(),
        })}>
        {(formik) => {
          const role = formik.values.projectRole;

          return (
            <Form>
              <p>
                Set the role of {ids.length} {pluralize('time entry', ids.length)} on <strong>{project.name}</strong>{' '}
                to:
              </p>

              <Form.Control>
                <Field.ProjectRoleSelect
                  name="projectRole"
                  placeholder="Role"
                  project={project}
                  component={RoleDropdown}
                  renderOption={(option) => (
                    <RoleOption>
                      <BillableIcon value={option.isActuallyBillable} />
                      <Tooltip message={option.name} placement="right">
                        {option.name}
                      </Tooltip>
                    </RoleOption>
                  )}
                />
              </Form.Control>

              {role && (
                <BillingNote>
                  <strong>{role.name}</strong> is {role.isActuallyBillable ? 'billable' : 'non-billable'}. Time entries
                  without a billing override follow the role&apos;s billable setting and rate.
                </BillingNote>
              )}
            </Form>
          );
        }}
      </Formik>
    </BulkActionModal>
  );
}
